/**
 * CalculatorStepRail — big numbered pills across the top of a calculator page.
 *
 * Sticky sub-nav that reflects the user's progress through a multi-section
 * calculator. Highlights the currently-visible section via scroll spy and lets
 * the user jump forwards/backwards with a click.
 *
 * Usage:
 *   const [current, setCurrent] = useState(0);
 *   <CalculatorStepRail
 *     steps={[
 *       { id: 'section-plan', label: 'Product', helper: 'Name & description' },
 *       { id: 'section-batch', label: 'Batch', helper: 'Machine + trays' },
 *       ...
 *     ]}
 *     currentIndex={current}
 *     onStepClick={(i) => setCurrent(i)}
 *     completed={{ 0: true, 1: false, ... }}
 *   />
 */
import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

const CalculatorStepRail = ({
  steps = [],
  currentIndex = 0,
  onStepClick,
  completed = {},
  testId = 'calculator-step-rail',
}) => {
  // Suppress scroll-spy briefly after a programmatic jump so the pill the user
  // just clicked stays active instead of flickering through intermediate sections.
  const suppressUntilRef = useRef(0);

  useEffect(() => {
    if (!steps.length) return;
    const observers = [];
    steps.forEach((step, index) => {
      const el = document.getElementById(step.id);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (Date.now() < suppressUntilRef.current) return;
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
              onStepClick && onStepClick(index, { source: 'scroll' });
            }
          });
        },
        { threshold: [0.25, 0.5], rootMargin: '-120px 0px -40% 0px' },
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [steps.length]);

  const jumpTo = (index) => {
    onStepClick && onStepClick(index, { source: 'click' });
    // Suppress spy for ~700ms while smooth-scroll runs
    suppressUntilRef.current = Date.now() + 700;
    const el = document.getElementById(steps[index].id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (!steps.length) return null;

  return (
    <div
      className="sticky top-16 z-20 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-b border-border no-print"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-thin pb-1">
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          const isDone = completed[index];
          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                onClick={() => jumpTo(index)}
                data-testid={`step-rail-${index}`}
                data-active={isActive}
                className={`group flex items-center gap-3 rounded-full border-2 px-3.5 py-2 transition-all flex-shrink-0 ${
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground shadow-md'
                    : isDone
                      ? 'border-primary/40 bg-primary/10 text-foreground hover:border-primary/60'
                      : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
                }`}
              >
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full font-heading text-sm font-extrabold ${
                    isActive
                      ? 'bg-primary-foreground text-primary'
                      : isDone
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground/70'
                  }`}
                >
                  {isDone && !isActive ? <Check className="w-3.5 h-3.5" /> : index + 1}
                </span>
                <span className="text-left leading-tight hidden sm:block">
                  <span className={`block text-sm font-heading font-extrabold whitespace-nowrap ${
                    isActive ? 'text-primary-foreground' : 'text-foreground'
                  }`}>
                    {step.label}
                  </span>
                  {step.helper && (
                    <span className={`block text-[11px] whitespace-nowrap ${
                      isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    }`}>
                      {step.helper}
                    </span>
                  )}
                </span>
                <span className="text-sm font-heading font-extrabold whitespace-nowrap sm:hidden">
                  {step.label}
                </span>
              </button>
              {index < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={`hidden sm:block h-px w-6 lg:w-10 flex-shrink-0 ${
                    isDone ? 'bg-primary' : 'bg-border'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CalculatorStepRail;
